import { Box, Stack } from "@mui/material"
import { forwardRef, useEffect } from "react"
import { usePopupContext } from "../hooks/PopupContext"
import { useAuth } from "../hooks/useAuth"
import { useMixPanel } from "../hooks/useMixPanel"
import { PopupAndCardConfig } from "@/types/components/popup"

interface BizzaboCustomFormProps {
  data: PopupAndCardConfig
  handlePopupAttributeUpdate: (type: 'skip' | 'close') => Promise<void>
}

const BizzaboCustomForm = forwardRef<Record<string, unknown>, BizzaboCustomFormProps>(({ data, handlePopupAttributeUpdate }, ref) => {
  // @ts-expect-error untyped context value
  const { setPopupAndCardState } = usePopupContext()
  const isFormSubmittedRef = ref
  // @ts-expect-error untyped auth value
  const { userInfo } = useAuth()
  const { logEvent } = useMixPanel()

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin === 'https://events.bizzabo.com') {
        // Handle different message types
        if (event.data.type === "closeRSVP") {
          setPopupAndCardState(prev => {
            const events = prev.qualifiedEvents
            const index = events.findIndex(item => item.id === data.contentId)
            events[index].isCustomFormPopupActive = false

            return { ...prev, qualifiedEvents: events }
          })
          if (isFormSubmittedRef && typeof isFormSubmittedRef === 'object' && isFormSubmittedRef.current[`${data.contentId}-submitted`]) return
          await handlePopupAttributeUpdate('skip')

          return
        } else if (event.data.type === "registrationCompleted") {
          await handlePopupAttributeUpdate('close')
          logEvent('submit_bizzabo_form', {
            unicity_id: userInfo?.unicityId,
            country_code: userInfo?.countryCode,
            popup_content_id: data?.contentId,
          })
          return
        }
      }
    };

    window.addEventListener('message', handleMessage);
    // log event when the popup is loaded
    logEvent('bizzabo_popup_loaded', {
      unicity_id: userInfo?.unicityId,
      country_code: userInfo?.countryCode,
      popup_content_id: data?.contentId,
      is_qualified: true
    })

    // Cleanup: remove event listener when component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [data?.contentId, handlePopupAttributeUpdate, isFormSubmittedRef, logEvent, setPopupAndCardState, userInfo?.countryCode, userInfo?.unicityId])

  return (
    <Box
      data-test-id="popup-modal-content"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 40px)',
        minWidth: '220px',
        maxWidth: data?.styles?.maxWidth || '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: "12px",
        outline: 'none',
      }}
    >
      <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        <Stack
          sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
          px={3}
          pb={5}
        >
          <iframe
            title="Bizzabo Custom Form"
            className="rsvp-frame"
            style={{
              width: '100%',
              maxHeight: '70vh',
              minHeight: '50vh',
              border: 'none',
            }}
            id="rsvp-frame-6120266"
            src="https://events.bizzabo.com/flows/events/735147/flow/bca0fa70-86cc-427d-8c10-a686a7d4ab04?"
          />
        </Stack>
      </Box>
    </Box>
  )
})

BizzaboCustomForm.displayName = 'BizzaboCustomForm'

export default BizzaboCustomForm